/**
 * AdminFeedback — Inbox for user-submitted feedback ("Flag an issue / suggest").
 *
 * Admin-gated by the backend (`await require_admin(request)`). This page just
 * renders the list with status filters + search + status-change actions.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Inbox, Search, Trash2, Loader2, ChevronDown, ChevronRight, CheckCircle2,
  AlertTriangle, Clock, MessageSquare, ExternalLink,
} from "lucide-react";

const STATUSES = [
  { id: "all", label: "All" },
  { id: "new", label: "New", icon: AlertTriangle, color: "#E58B25" },
  { id: "triaged", label: "Triaged", icon: Clock, color: "#4CA6A8" },
  { id: "actioned", label: "Actioned", icon: CheckCircle2, color: "#1A6F2B" },
  { id: "dismissed", label: "Dismissed", icon: Trash2, color: "#7A7A75" },
];

const AdminFeedback = () => {
  const nav = useNavigate();
  const { user, ready } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("new");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [openIds, setOpenIds] = useState(() => new Set());
  const [notes, setNotes] = useState({}); // per-id textarea buffer

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/feedback?${params.toString()}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Admin access required" : "Failed to load");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!ready) return;
    if (!user) { nav("/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user, status]);

  const toggle = (id) => setOpenIds((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const setStatusFor = async (id, newStatus) => {
    try {
      const note = notes[id]?.trim() || undefined;
      const r = await api.patch(`/admin/feedback/${id}`, { status: newStatus, admin_note: note });
      setItems((xs) => xs.map((it) => (it.id === id ? r.data : it)));
      // Refresh counts + re-filter if the item no longer matches the current tab.
      await load();
      toast.success(`Marked ${newStatus}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/feedback/${id}`);
      setItems((xs) => xs.filter((it) => it.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const StatusPill = ({ s }) => {
    const meta = STATUSES.find((x) => x.id === s) || { label: s, color: "#7A7A75" };
    return (
      <span className="pill" style={{ background: "#F6F5F2", color: meta.color, border: `1px solid ${meta.color}` }}
              data-testid={`fb-status-pill-${s}`}>
        {meta.label}
      </span>
    );
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Inbox size={24} style={{ color: "var(--brand-green)" }} />
          <h1 className="font-display text-3xl" style={{ color: "var(--char)" }} data-testid="admin-feedback-title">
            Feedback inbox
          </h1>
        </div>
        <p className="text-sm mb-6" style={{ color: "var(--muted)" }}>
          User submissions from the "Flag an issue / suggest an improvement" form.
        </p>

        {/* Status tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="fb-status-tabs">
          {STATUSES.map((s) => (
            <button key={s.id}
                    onClick={() => setStatus(s.id)}
                    data-testid={`fb-tab-${s.id}`}
                    className="btn-secondary !py-2 !px-3 text-sm"
                    style={status === s.id ? { background: "var(--brand-green)", color: "#fff", borderColor: "var(--brand-green)" } : {}}>
              {s.label}
              <span className="ml-2 text-xs" style={{ opacity: 0.8 }}>{counts[s.id] ?? 0}</span>
            </button>
          ))}
          <div className="flex-1" />
          <div className="flex items-center gap-2 rounded" style={{ background: "#fff", border: "1px solid var(--rule)", padding: "6px 10px" }}>
            <Search size={14} style={{ color: "var(--muted)" }} />
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search text, email, project id…"
              className="bg-transparent outline-none text-sm"
              data-testid="fb-search-input"
            />
            <button onClick={load} className="text-xs underline" style={{ color: "var(--brand-green)" }} data-testid="fb-search-btn">
              Go
            </button>
          </div>
        </div>

        {busy && filtered.length === 0 ? (
          <div className="card p-8 flex items-center justify-center gap-3" style={{ color: "var(--muted)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading feedback…
          </div>
        ) : filtered.length === 0 ? (
          <div className="card p-8 text-center" data-testid="fb-empty" style={{ color: "var(--muted)" }}>
            <MessageSquare size={20} className="mx-auto mb-2" />
            No feedback in this bucket. Come back later.
          </div>
        ) : (
          <div className="space-y-2" data-testid="fb-list">
            {filtered.map((it) => {
              const open = openIds.has(it.id);
              return (
                <div key={it.id} className="card" data-testid={`fb-row-${it.id}`}>
                  <button
                    type="button"
                    onClick={() => toggle(it.id)}
                    className="w-full flex items-start gap-3 px-5 py-4 text-left"
                    data-testid={`fb-toggle-${it.id}`}
                  >
                    {open ? <ChevronDown size={16} className="mt-1" /> : <ChevronRight size={16} className="mt-1" />}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusPill s={it.status} />
                        <span className="text-xs" style={{ color: "var(--muted)" }}>
                          {new Date(it.created_at).toLocaleString()}
                        </span>
                        {it.email && (
                          <span className="text-xs" style={{ color: "var(--brand-green)" }}>{it.email}</span>
                        )}
                        {it.project_id && (
                          <span className="text-xs font-mono" style={{ color: "var(--muted)" }}>#{String(it.project_id).slice(-6)}</span>
                        )}
                      </div>
                      <div className="mt-1 text-sm line-clamp-2" style={{ color: "var(--char)" }}>
                        <b>Got:</b> {it.got}
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div className="px-5 pb-5 pt-1 border-t border-[var(--rule)] mt-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="overline mb-1">Expected</div>
                        <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--char)" }}>{it.expected}</div>
                      </div>
                      <div>
                        <div className="overline mb-1">Got</div>
                        <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--char)" }}>{it.got}</div>
                      </div>
                      <div className="md:col-span-2 text-xs" style={{ color: "var(--muted)" }}>
                        <b>Context:</b> {JSON.stringify(it.context || {})}<br />
                        <b>User-Agent:</b> {it.user_agent}
                        {it.project_id && (
                          <span className="ml-2">
                            · <a href={`/results?run=${it.run_id || ""}`} className="underline inline-flex items-center gap-1" style={{ color: "var(--brand-green)" }}>
                              open project <ExternalLink size={11} />
                            </a>
                          </span>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <textarea
                          rows={2}
                          placeholder="Admin note (optional — saved with the next status change)…"
                          value={notes[it.id] ?? (it.admin_note || "")}
                          onChange={(e) => setNotes((n) => ({ ...n, [it.id]: e.target.value }))}
                          className="w-full px-3 py-2 border border-[var(--rule)] rounded text-sm"
                          data-testid={`fb-note-${it.id}`}
                        />
                      </div>
                      <div className="md:col-span-2 flex flex-wrap gap-2">
                        {STATUSES.filter((s) => s.id !== "all" && s.id !== it.status).map((s) => (
                          <button
                            key={s.id}
                            onClick={() => setStatusFor(it.id, s.id)}
                            data-testid={`fb-action-${s.id}-${it.id}`}
                            className="btn-secondary !py-1.5 !px-3 text-xs"
                            style={{ borderColor: s.color, color: s.color }}
                          >
                            Mark {s.label.toLowerCase()}
                          </button>
                        ))}
                        <div className="flex-1" />
                        <button
                          onClick={() => remove(it.id)}
                          data-testid={`fb-delete-${it.id}`}
                          className="btn-secondary !py-1.5 !px-3 text-xs"
                          style={{ borderColor: "#C33", color: "#C33" }}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AdminFeedback;
